import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BokkzClient, ClientEntity, IntermediaryEntity } from '../services/api.bokkz.services';
import { CustomerQueryClient, RelationSearchCriteria, RelationSearchResults } from '../services/rekenclient.api';
import { FormBuilder } from '@angular/forms';
import { ToastService } from '@snelstart/ui-toast';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { BokkzAlgDataService } from '../services/bokkz-alg-data-service';
import { firstValueFrom, of, Subject, takeUntil } from 'rxjs';
import { ButtonConfiguration, SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { SharedUiTabModule } from '@snelstart/ui-tab';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { CommonModule } from '@angular/common';
import { BokkzAlgemeenClient, ClientMerge } from '../services/api.bokkz.algemeen';
import { BokkzAlgLogic } from '../services/bokkz-alg-logic';
import { dispatchDeletedItemEvent, SearchCriteria } from '@snelstart/domain-logic';

interface TaxItem {
    taxId?: string;
    taxIdIncomeTax?: string;
    taxIdVat?: string;
    taxIdWageTax?: string;
    taxIdCorporateTax?: string;
    valueAddedTax?: boolean;
    postponement?: boolean;
    corporateTax?: boolean;
    incomeTax?: boolean;
    wageTax?: boolean;
    kvkId?: string;
    classificatie?: string;
    sbiCode?: string;
    dossiers?: boolean;
}

type TaxKey =
    | 'valueAddedTax'
    | 'incomeTax'
    | 'corporateTax'
    | 'wageTax'
    | 'dossiers'
    | 'bezwaar'
    | 'salaris'
    | 'dividend'
    | 'gifttax'
    | 'digipoort'
    | 'fiscalIdentifier'
    ;

@Component({
    selector: 'snelstart-venture-fiscaal-domain-logic-client-management',
    templateUrl: './client-management.component.html',
    imports: [
        SharedUiButtonModule,
        SharedUiFormControlsModule,
        SharedUiDetailsModule,
        SharedUiDialogModule,
        SharedUiTabModule,
        SharedUiAlertModule,
        CommonModule
    ],
    styleUrl: './client-management.component.css',
    standalone: true
})
export class ClientManagementComponent implements OnInit, OnDestroy {
    @Input() public dialogVisible = false;
    @Output() public dialogVisibleChange = new EventEmitter<boolean>();

    private destroy$ = new Subject<void>();
    public loading = false;
    public error?: any;
    public targetId?: string;
    public sourceIds = new Set<string>();
    public relations = {} as RelationSearchResults;
    public clientId = {} as number;
    public selectedClient = {} as ClientEntity;
    public selectedTab = 0;
    public providerId = {} as string;
    public focusedElementId: string | null = null;
    public index = 0;
    public intermediary = {} as IntermediaryEntity;
    public showSamenvoegklanten = true;
    public selectedCurrentUserId?: string;
    public selectedArchivedUserId?: string;
    archivedUsers = {} as RelationSearchResults;
    archivedSearchTerm = '';
    activeSearchTerm = '';
    taxSelection: Record<TaxKey, boolean> & { [key: string]: boolean } = {
        valueAddedTax: false,
        incomeTax: false,
        corporateTax: false,
        wageTax: false,
        dossiers: false,
        bezwaar: false,
        salaris: false,
        dividend: false,
        gifttax: false,
        digipoort: false,
        fiscalIdentifier: false
    };

    public juisteKlanten = false;
    public targetName: string = '';
    public sourceName: string = '';
    public allowMultipleSources = false;

    taxItemsToShow: Array<{ key: string; label: string; enabled: boolean }> = [];

    public optionsJaNee: SelectFormControlItem<boolean>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' }
    ];

    setTab(tab: number) {
        this.selectedTab = tab;

        if (tab === 1) {
            this.loadArchivedUsers();
        }
    }

    get isSplitTab() {
        return this.selectedTab === 1;
    }

    get isMergeTab() {
        return this.selectedTab === 0;
    }

    constructor(
        private bokkzClient: BokkzClient,
        public toastService: ToastService,
        private bokkzAlgData: BokkzAlgDataService,
        private customerQueryClient: CustomerQueryClient,
        private bokkzAlgLogic: BokkzAlgLogic,
        private bokkzAlgemeenClient: BokkzAlgemeenClient,
        private injector: Injector,
        public fb: FormBuilder
    ) {
    }

    ngOnInit() {
        this.bokkzAlgData
            .getRelations()
            .pipe(takeUntil(this.destroy$))
            .subscribe((relations) => {
                if (relations) {
                    this.relations = relations;
                }
            });
        this.bokkzAlgData.intermediary.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.intermediary = value;
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public mergeButtonConfig = ButtonConfiguration.create({
        onClick: () => {
            this.mergeSelected();
            return of(null); // Return an Observable as required
        },
        canExecute: () => !!this.targetId && this.sourceIds.size > 0
    });

    public mergeSelected() {
        if (!this.targetId || this.sourceIds.size === 0) {
            return;
        }

        const payloadMerge = {
            targetId: this.targetId,
            sourceIds: Array.from(this.sourceIds)
        } as ClientMerge;

        this.bokkzAlgemeenClient.postClientMerge(payloadMerge).subscribe({
            next: (mergeResponse) => {
                if (payloadMerge.sourceIds) {
                    for (const item of payloadMerge.sourceIds!) {
                        this.bokkzAlgLogic.archiveCustomer(item).subscribe({
                            next: () => {
                                dispatchDeletedItemEvent({ identifier: item });
                            },
                            error: () => {
                                // this.toastService.showErrorMessage('Samenvoegen mislukt.');
                            }
                        });
                        //   // dispatchDeletedItemEvent({identifier: item})
                    }
                    console.log(payloadMerge);
                }
            },
            error: (error) => {
                if (error.message) {
                    this.toastService.showErrorMessage(`Samenvoegen mislukt: ${error.message}`);
                } else {
                    // this.toastService.showErrorMessage('Samenvoegen mislukt.');
                }
            },
            complete: () => {
                this.relations.results = this.relations.results.filter((item) => item.id === this.targetId || !this.sourceIds.has(item.id));
                this.sourceIds.clear();
                this.checkNameOverlap();
                this.toastService.showSuccessMessage(
                    ' Het samenvoegen is gelukt. De gearchiveerde klant vind je onder "Gearchiveerde klanten".'
                );
            }
        });
    }

    onSplit() {
        if (!this.selectedClient) return;

        if (!this.selectedCurrentUserId || !this.selectedArchivedUserId) return;

        const payloadSplit = {
            currentUserId: this.selectedCurrentUserId,
            archivedUserId: this.selectedArchivedUserId,
            taxSelection: this.taxSelection
        };
        this.bokkzAlgemeenClient.postClientSplit(payloadSplit).subscribe({
            next: (mergeResponse) => {
                if (payloadSplit.currentUserId) {
                    this.bokkzAlgLogic.deArchiveCustomer(payloadSplit.archivedUserId).subscribe({
                        next: () => {
                            this.toastService.showSuccessMessage('Archiveren gelukt.');
                        },
                        error: () => {
                            this.toastService.showErrorMessage('Archiveren mislukt.');
                        },
                    });
                }
            },
            error: (error) => {
                if (error.message) {
                    this.toastService.showErrorMessage(`Archiveren mislukt: ${error.message}`);
                } else {
                    this.toastService.showErrorMessage('Archiveren mislukt.');
                }
            },
            complete: () => {
                this.relations.results = this.relations.results.filter((item) => item.id === this.targetId || !this.sourceIds.has(item.id));
                this.sourceIds.clear();
                this.checkNameOverlap();
                this.toastService.showSuccessMessage('Het samenvoegen is gelukt en de klant is geactiveerd.');
            },
        });
    }

    public selectTarget(id: string) {
        this.targetId = id;
        this.sourceIds.delete(id);
        this.checkNameOverlap();
    }

    private checkNameOverlap(): void {
        this.juisteKlanten = false;
        this.targetName = '';
        this.sourceName = '';

        if (!this.targetId || this.sourceIds.size === 0) {
            return;
        }

        const targetRelation = this.relations.results.find((r) => r.id === this.targetId);
        if (!targetRelation) {
            return;
        }

        // Sla target naam op
        this.targetName = targetRelation.name;

        // Check alle sources tegen de target
        for (const sourceId of this.sourceIds) {
            const sourceRelation = this.relations.results.find((r) => r.id === sourceId);
            if (sourceRelation) {
                // Sla source naam op
                this.sourceName = sourceRelation.name;

                if (!this.hasMinimalNameOverlap(targetRelation.name, sourceRelation.name, 4)) {
                    this.juisteKlanten = true;
                    return; // Stop bij eerste mismatch
                }
            }
        }
    }

    private removeCommonSuffixes(name: string): string {
        if (!name) return '';

        // Lijst met uit te sluiten termen (case-insensitive)
        const exclusions = ['b.v.', 'bv', 'v.o.f.', 'vof', 'van', 'der'];

        let cleaned = name.toLowerCase().trim();

        // Verwijder alle exclusions
        for (const exclusion of exclusions) {
            // Verwijder aan het einde
            cleaned = cleaned.replace(new RegExp(`\\s*${exclusion.replace(/\./g, '\\.')}\\s*$`, 'gi'), '');
            // Verwijder aan het begin
            cleaned = cleaned.replace(new RegExp(`^\\s*${exclusion.replace(/\./g, '\\.')}\\s*`, 'gi'), '');
            // Verwijder in het midden (met spaties eromheen)
            cleaned = cleaned.replace(new RegExp(`\\s+${exclusion.replace(/\./g, '\\.')}\\s+`, 'gi'), ' ');
        }

        return cleaned.trim();
    }

    private hasMinimalNameOverlap(name1: string, name2: string, minChars: number = 4): boolean {
        if (!name1 || !name2) return false;

        // Filter eerst de namen
        const cleanName1 = this.removeCommonSuffixes(name1);
        const cleanName2 = this.removeCommonSuffixes(name2);

        const normalize = (str: string) => str.toLowerCase().trim();
        const n1 = normalize(cleanName1);
        const n2 = normalize(cleanName2);

        // Zoek naar overlappende opeenvolgende substrings van minimaal minChars lengte
        for (let i = 0; i <= n1.length - minChars; i++) {
            const substring = n1.substring(i, i + minChars);
            if (n2.includes(substring)) {
                return true;
            }
        }

        return false;
    }

    public async toggleSource(id: string, checked: boolean) {
        if (checked) {
            if (!this.allowMultipleSources) {
                this.sourceIds.clear();
            }

            try {
                const response = await firstValueFrom(this.customerQueryClient.getCustomer(id));

                if (!response.memo?.toLowerCase().includes('klant accountancyportaal')) {
                    this.sourceIds.delete(id);
                    this.toastService.showErrorMessage('Dit betreft een klant uit de Relatie klantenlijst binnen Polaris. Deze klant kan niet gearchiveerd worden.');
                    return;
                }
                this.sourceIds.add(id);

            } catch (error) {
                console.error('Error fetching customer', error);
                this.toastService.showErrorMessage('Kon klantinformatie niet ophalen');
                return;
            }
        } else {
            this.sourceIds.delete(id);
        }
        this.checkNameOverlap();
    }

    public onToggleSourceChange(event: Event, id: string) {
        const checkbox = event.target as HTMLInputElement;
        const intendedState = checkbox.checked;
        const helpdesk = 1002848 ===this.intermediary.id

        if (intendedState) {
            if (!this.allowMultipleSources && !helpdesk) {
                this.sourceIds.clear();
            }

            this.customerQueryClient.getCustomer(id).subscribe({
                next: (response) => {
                    if (!response.memo?.toLowerCase().includes('klant accountancyportaal') && !helpdesk) {
                        this.toastService.showErrorMessage('Dit betreft een klant uit de Relatie-klantenlijst binnen Polaris. Deze klant kan niet gearchiveerd worden.');
                        checkbox.checked = false;
                        return;
                    }

                    // Passed check → add to sourceIds
                    this.sourceIds.add(id);
                    this.checkNameOverlap();
                },
                error: (err) => {
                    console.error('Error fetching customer', err);
                    this.toastService.showErrorMessage('Kon klantinformatie niet ophalen');
                    // Undo toggle
                    checkbox.checked = false;
                }
            });
        } else {
            // Uncheck
            this.sourceIds.delete(id);
            this.checkNameOverlap();
        }
    }


    prepareTaxItems(client?: any) {
        // Reset all selections
        Object.keys(this.taxSelection).forEach((key) => (this.taxSelection[key as TaxKey] = false));

        // Conditional selections based on client entity
        this.taxSelection.valueAddedTax = !!client?.valueAddedTax;
        this.taxSelection.incomeTax = !!client?.incomeTax;
        this.taxSelection.corporateTax = !!client?.corporateTax;
        this.taxSelection.wageTax = !!client?.wageTax;

        // Always available
        this.taxSelection.dossiers = true;
        this.taxSelection.bezwaar = true;
        this.taxSelection.digipoort = true;

        // Derived from client entity
        this.taxSelection.salaris = !!client?.wageTax;
        this.taxSelection.dividend = !!client?.corporateTax;
        this.taxSelection.gifttax = !!client?.incomeTax;

        this.taxItemsToShow = [
            { key: 'bezwaar' as TaxKey, label: 'Bezwaar', enabled: true },
            { key: 'digipoort' as TaxKey, label: 'Digipoort', enabled: true },
            { key: 'dividend' as TaxKey, label: 'DividendBelasting', enabled: !!client?.corporateTax },
            { key: 'dossiers' as TaxKey, label: 'Dossiers', enabled: true },
            { key: 'incomeTax' as TaxKey, label: 'Inkomstenbelatsing', enabled: !!client?.incomeTax },
            { key: 'valueAddedTax' as TaxKey, label: 'Omzetbelasting', enabled: !!client?.valueAddedTax },
            { key: 'salaris' as TaxKey, label: 'Salaris', enabled: !!client?.wageTax },
            { key: 'gifttax' as TaxKey, label: 'Schenkbelasting', enabled: !!client?.incomeTax },
            { key: 'corporateTax' as TaxKey, label: 'Vennootschapsbelasting', enabled: !!client?.corporateTax }
        ];
    }

    selectCurrentUser(activeUser: string) {
        this.selectedCurrentUserId = activeUser;

        this.bokkzClient.getClient(activeUser).subscribe((clientEntity: ClientEntity) => {
            this.selectedClient = clientEntity;
            this.prepareTaxItems(clientEntity);
        });
    }

    selectArchivedUser(userId: string) {
        this.selectedArchivedUserId = userId;
    }

    showKlanten() {
        this.showSamenvoegklanten = !this.showSamenvoegklanten;
    }

    async loadArchivedUsers(): Promise<void> {
        this.archivedUsers = new RelationSearchResults();
        try {
            const criteria = new SearchCriteria();
            criteria.columnFilters ??= { alphaNumeric: [], numeric: [], date: [] };
            criteria.columnFilters.alphaNumeric.push({
                columnName: 'IsActive',
                value: 'False'
            });

            const relationCriteria = RelationSearchCriteria.fromSearchCriteria(criteria, false, false);

            this.archivedUsers = await firstValueFrom(this.bokkzAlgLogic.getPaginated(relationCriteria));
        } catch (error) {
            console.error('Failed to load archived users', error);
            this.archivedUsers = new RelationSearchResults();
        }
    }

    async searchArchivedUsers(isActive: boolean) {
        const criteria = new SearchCriteria();
        criteria.columnFilters ??= { alphaNumeric: [], numeric: [], date: [] };

        if (isActive) {
            criteria.columnFilters.alphaNumeric.push({
                columnName: 'IsActive',
                value: 'True'
            });
            if (this.activeSearchTerm) {
                criteria.searchTerm = this.activeSearchTerm;
            }
        } else {
            criteria.columnFilters.alphaNumeric.push({
                columnName: 'IsActive',
                value: 'False'
            });
            if (this.archivedSearchTerm) {
                criteria.searchTerm = this.archivedSearchTerm;
            }
        }

        if (this.archivedSearchTerm) {
            criteria.searchTerm = this.archivedSearchTerm;
        }

        const relationCriteria = RelationSearchCriteria.fromSearchCriteria(criteria, false, false);
        if (isActive) {
            this.relations = await firstValueFrom(this.bokkzAlgLogic.getPaginated(relationCriteria));
        } else {
            this.archivedUsers = await firstValueFrom(this.bokkzAlgLogic.getPaginated(relationCriteria));
        }
    }
}
